const STORAGE_KEY = 'scoutlytics_active_analysis_id';

export interface ActiveAnalysisRouteState {
  analysisId?: string;
  backgroundMode?: boolean;
  isLongVideo?: boolean;
  estimatedTime?: string;
}

/** Canonical analyzing route with upload ID in the query string (survives refresh). */
export function buildAnalyzingPath(analysisId: string): string {
  return `/analyzing?analysisId=${encodeURIComponent(analysisId)}`;
}

/** Persist the active upload ID so /analyzing survives refresh and direct links. */
export function persistActiveAnalysisId(analysisId: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, analysisId);
  } catch {
    // ignore private browsing / quota errors
  }
}

export function readPersistedAnalysisId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearPersistedAnalysisId(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

interface ResolveOptions {
  routeState?: ActiveAnalysisRouteState | null;
  searchParams?: URLSearchParams;
  contextAnalysisIds?: string[];
}

/**
 * Resolve active analysis ID — same priority order as floating status indicator needs.
 */
export function resolveActiveAnalysisId({
  routeState,
  searchParams,
  contextAnalysisIds = [],
}: ResolveOptions): string | null {
  const fromState = routeState?.analysisId?.trim();
  if (fromState) return fromState;

  const fromQuery =
    searchParams?.get('analysisId')?.trim() ||
    searchParams?.get('uploadId')?.trim() ||
    searchParams?.get('id')?.trim();
  if (fromQuery) return fromQuery;

  const fromStorage = readPersistedAnalysisId()?.trim();
  if (fromStorage) return fromStorage;

  const fromContext = contextAnalysisIds.find((id) => id?.trim());
  if (fromContext) return fromContext.trim();

  return null;
}

/** Dev-only trace for analyzing ID resolution (no-op in production). */
export function logActiveAnalysisIdResolution(
  resolvedId: string | null,
  sources: {
    routeStateId?: string | null;
    queryId?: string | null;
    storageId?: string | null;
    contextId?: string | null;
  }
): void {
  if (process.env.NODE_ENV !== 'development') return;
  console.debug('[Scoutlytics] Analyzing ID resolution', {
    resolvedId,
    ...sources,
  });
}
